import { useCallback, useEffect } from 'react'

import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'

import tracker from 'src/lib/js/tracker'

interface TrackerConfig {
  debug?: boolean
  blacklisted?: boolean
}

/**
 * A convenient hook for initialization and accessing the singleton Tracker instance.
 *
 * @param {object} options - An optional object containing the configuration for the tracker instance. Should only be called with
 *   options once at the highest level possible in your app. Has the following keys:
 *   @prop {boolean} blacklisted - A truthy/falsey value indicating whether the current environment/client should be tracked.
 *   @prop {boolean} debug - A truthy value will enable logging during tracking events.
 */
export const useTracker = (options?: TrackerConfig) => {
  useEffect(() => {
    if (options) {
      tracker.debug = options.debug ? options.debug : tracker.debug
      tracker.blacklisted = options.blacklisted
        ? options.blacklisted
        : tracker.blacklisted
    }
  }, [options])

  return tracker
}

export const useThrottledTracker = () => {
  const { ooTrackingDisabled } = useFlags()
  const tracker = useTracker()

  // use the FF to determine if events should be tracked with amplitude
  const track = useCallback(
    (name: any, data: any) => {
      tracker?.track(name, data, ooTrackingDisabled)
    },
    [ooTrackingDisabled, tracker]
  )

  return { track }
}

export default useTracker
